import React, { useEffect, useState } from "react";
import { X, Network, Users, Briefcase, Sparkles, AlertTriangle } from "lucide-react";
import api from "../lib/api";

/**
 * Cross-Pollination consent modal.
 * The promise: this NEVER pulls in another brand's data without an explicit "yes" right here.
 *
 * Props:
 *  - open: boolean
 *  - brandId: string (the brand being generated for)
 *  - onConfirm({ pollination_brand_ids, pollination_mode }) — proceed
 *  - onClose() — cancel/skip pollination
 */
export default function CrossPollinationModal({ open, brandId, onConfirm, onClose }) {
  const [connections, setConnections] = useState([]); // existing connections for this brand
  const [allBrands, setAllBrands] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState("passive");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([api.listConnections(brandId), api.listBrands()])
      .then(([c, b]) => {
        setConnections(c || []);
        setAllBrands(b || []);
        setSelected(new Set()); // start unchecked — explicit consent required
      })
      .finally(() => setLoading(false));
  }, [open, brandId]);

  if (!open) return null;

  const partners = connections
    .filter((c) => c.enabled !== false)
    .map((c) => ({
      conn: c,
      brand: allBrands.find((b) => b.brand_id === c.target_brand_id),
    }))
    .filter((x) => x.brand);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const confirm = () => {
    onConfirm({
      pollination_brand_ids: Array.from(selected),
      pollination_mode: mode,
    });
  };

  const skip = () => {
    onConfirm({ pollination_brand_ids: [], pollination_mode: "passive" });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="xpollinate-modal">
      <div className="bg-white border-2 border-black max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black bg-[#FAFAFA]">
          <div className="flex items-center gap-2">
            <Network size={18} />
            <div className="text-eyebrow">Cross-pollination consent</div>
          </div>
          <button onClick={onClose} className="hover:text-[#E11D48]" data-testid="xpollinate-close"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-5">
          <h2 className="font-display font-black tracking-tighter text-3xl">Pull context from your other brands?</h2>
          <p className="text-sm text-[#52525B] leading-relaxed">
            By default, this generation only uses THIS brand's voice, knowledge, interviews, and flags. If you want, you can hand-pick other brands (your own or client) to inform this post. <strong className="text-black">Nothing happens without you ticking the boxes.</strong>
          </p>

          {loading ? (
            <div className="text-sm mono uppercase text-[#A1A1AA]">loading connected brands…</div>
          ) : partners.length === 0 ? (
            <div className="border border-dashed border-[#E2E4E9] p-5 text-sm text-[#52525B]">
              No brand connections found. To enable cross-pollination, first go to <strong>Connect</strong> tab on this brand and add a connection to another brand you own or manage.
            </div>
          ) : (
            <div className="border border-black">
              <div className="grid grid-cols-12 gap-3 px-4 py-2 text-eyebrow border-b border-black bg-[#FAFAFA]">
                <div className="col-span-1"></div>
                <div className="col-span-4">Brand</div>
                <div className="col-span-2">Kind</div>
                <div className="col-span-5">Sells</div>
              </div>
              {partners.map(({ conn, brand }) => (
                <label
                  key={brand.brand_id}
                  className={`grid grid-cols-12 gap-3 px-4 py-3 text-sm cursor-pointer border-b border-[#E2E4E9] last:border-0 ${
                    selected.has(brand.brand_id) ? "bg-[#FFFBEB]" : "hover:bg-[#FAFAFA]"
                  }`}
                  data-testid={`xpollinate-row-${brand.brand_id}`}
                >
                  <div className="col-span-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={selected.has(brand.brand_id)}
                      onChange={() => toggle(brand.brand_id)}
                      className="w-4 h-4 accent-black"
                      data-testid={`xpollinate-checkbox-${brand.brand_id}`}
                    />
                  </div>
                  <div className="col-span-4 font-semibold flex items-center gap-2">
                    <span className="inline-block w-2 h-2" style={{ background: brand.color || "#000" }} />
                    {brand.name}
                  </div>
                  <div className="col-span-2 text-xs">
                    {brand.is_client ? (
                      <span className="flex items-center gap-1 text-[#B45309]"><Briefcase size={12}/> Client</span>
                    ) : (
                      <span className="flex items-center gap-1 text-[#0A0A0B]"><Users size={12}/> Own</span>
                    )}
                  </div>
                  <div className="col-span-5 text-xs text-[#52525B] truncate">{brand.sells || "—"}</div>
                </label>
              ))}
            </div>
          )}

          {/* Mode toggle always visible — reinforces the consent contract */}
          <div>
            <div className="text-eyebrow mb-2">Mode</div>
            <div className="grid md:grid-cols-2 gap-3">
              <button
                onClick={() => setMode("passive")}
                data-testid="xpollinate-mode-passive"
                className={`border-2 p-4 text-left transition-colors ${mode === "passive" ? "border-black bg-[#FAFAFA]" : "border-[#E2E4E9] hover:border-black"}`}
              >
                <div className="font-semibold mb-1">Passive (default)</div>
                <div className="text-xs text-[#52525B] leading-relaxed">Use their voice & worldview as inspiration. <strong>Do not mention them by name.</strong></div>
              </button>
              <button
                onClick={() => setMode("active")}
                data-testid="xpollinate-mode-active"
                className={`border-2 p-4 text-left transition-colors ${mode === "active" ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-[#E2E4E9] hover:border-black"}`}
              >
                <div className="font-semibold mb-1 flex items-center gap-2">Active <Sparkles size={14} className="text-[#F59E0B]"/></div>
                <div className="text-xs text-[#52525B] leading-relaxed">Actively weave & <strong>mention these brands by name</strong> — cross-promo.</div>
              </button>
            </div>
            {mode === "active" && selected.size > 0 && (
              <div className="mt-3 border border-[#F59E0B] bg-[#FFFBEB] p-3 text-xs flex gap-2">
                <AlertTriangle size={14} className="text-[#B45309] shrink-0 mt-0.5"/>
                <span>Active mode mentions partner brands by name. Best for cross-promo between your own brands or with explicit client permission.</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-black bg-[#FAFAFA]">
          <button onClick={skip} className="text-sm text-[#52525B] hover:text-black" data-testid="xpollinate-skip">
            Skip — generate solo for this brand
          </button>
          <button
            onClick={confirm}
            disabled={partners.length > 0 && selected.size === 0 && mode !== "passive"}
            className="bg-black text-white px-5 py-3 text-sm flex items-center gap-2 hover:bg-[#002FA7] disabled:opacity-40"
            data-testid="xpollinate-confirm"
          >
            <Sparkles size={14}/> Generate with {selected.size > 0 ? `${selected.size} partner${selected.size > 1 ? "s" : ""}` : "no partners"}
          </button>
        </div>
      </div>
    </div>
  );
}
